// Category (badge) names for the voice library and the /ai-voices hubs, in
// the page's language. Client-safe: utils/categoryHubs.ts is server code.
//
// Badges are free text from the badges cron (pages/api/cron/getBadges.js),
// so only the common ones have a translation (messages/library.ts, keyed by
// slug); the rest are mostly show and brand names and keep their own name.
import {
  intlLocale,
  translate,
  type Locale,
  type MessageKey,
} from "@jammable/ui-core/src/i18n";

// Same as slugifyBadge in utils/categoryHubs.ts (server-only, so not
// importable here); the message keys use this slug.
const badgeSlug = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export function titleCaseBadge(s: string): string {
  // Badges such as "fictional_character" showed as "Fictional_character AI
  // Voices" in the title and H1.
  return s
    .replace(/_+/g, " ")
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function lookup(locale: Locale, badge: string, kind: "cat" | "lang") {
  if (locale === "en") return null;
  const key = `library.${kind}.${badgeSlug(badge)}`;
  const s = translate(locale, key as MessageKey);
  // translate() returns the key itself when no language has it.
  return s === key ? null : s;
}

// The name as it reads mid-sentence: "Public Figure" in English (the badge,
// title-cased), "famosos" in Spanish and Portuguese.
export function categoryName(locale: Locale, badge: string): string {
  return (
    lookup(locale, badge, "cat") ??
    lookup(locale, badge, "lang") ??
    titleCaseBadge(badge)
  );
}

// The name on its own (a pill, breadcrumb or link): first letter capitalised.
export function categoryLabel(locale: Locale, badge: string): string {
  const name = categoryName(locale, badge);
  // Locale-aware: Turkish capitalises i as İ.
  return name.charAt(0).toLocaleUpperCase(intlLocale(locale)) + name.slice(1);
}

// Variables for the library.* strings that name a category (see the comment
// at the top of messages/library.ts).
export function categoryVars(locale: Locale, badge: string) {
  const name = categoryName(locale, badge);
  const isLanguage = lookup(locale, badge, "lang") !== null;
  return {
    category: name,
    categoryLower: locale === "en" ? name.toLowerCase() : name,
    of: translate(locale, isLanguage ? "library.ofLanguage" : "library.of", {
      name,
    }),
  };
}
